import type { CurrentUserDTO, Permission } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, WifiOff } from 'lucide-react';
import { type ReactNode, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { PageSpinner } from '../components/ui/Spinner';
import { api, ApiError, errorMessage } from '../lib/api';

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

type UserResponse = { user: CurrentUserDTO };

/** GET /api/auth/me — `null` when not signed in (401), so "logged out" is data, not an error. */
async function fetchCurrentUser(signal: AbortSignal): Promise<CurrentUserDTO | null> {
  try {
    return (await api.get<UserResponse>('/api/auth/me', { signal })).user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function useCurrentUserQuery() {
  return useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });
}

export type AuthApi = {
  /** Signed-in user, `null` when signed out, `undefined` while the first check is loading. */
  user: CurrentUserDTO | null | undefined;
  isLoading: boolean;
  /** UI-only permission check (the API enforces permissions server-side). */
  can: (permission: Permission) => boolean;
  /** Throws `ApiError` (401 wrong credentials, 423 locked, 429 rate limited) — show `error.message`. */
  login: (email: string, password: string) => Promise<CurrentUserDTO>;
  logout: () => Promise<void>;
  isLoggingIn: boolean;
};

export function useAuth(): AuthApi {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useCurrentUserQuery();

  const loginMutation = useMutation({
    mutationFn: (body: { email: string; password: string }) => api.post<UserResponse>('/api/auth/login', body),
    onSuccess: ({ user: signedIn }) => {
      // Drop anything cached for a previous user before seeding the new session.
      queryClient.clear();
      queryClient.setQueryData(AUTH_QUERY_KEY, signedIn);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    },
  });

  const can = useCallback((permission: Permission) => user?.permissions.includes(permission) ?? false, [user]);
  const { mutateAsync: loginAsync } = loginMutation;
  const login = useCallback(
    async (email: string, password: string) => (await loginAsync({ email, password })).user,
    [loginAsync],
  );
  const { mutateAsync: logoutAsync } = logoutMutation;
  const logout = useCallback(async () => {
    await logoutAsync();
  }, [logoutAsync]);

  return { user, isLoading, can, login, logout, isLoggingIn: loginMutation.isPending };
}

/**
 * Only same-site relative paths are allowed as post-login redirects (prevents open redirects via
 * `?next=//evil.example`).
 */
export function safeNextPath(next: string | null | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\') || next.startsWith('/login')) return '/';
  return next;
}

/** Route guard: renders children (or `<Outlet />`) for signed-in users, otherwise redirects to /login?next=. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const { data: user, isLoading, error, refetch, isRefetching } = useCurrentUserQuery();

  if (isLoading) return <FullScreen><PageSpinner label="Checking your session" /></FullScreen>;
  if (error && user === undefined) {
    return (
      <FullScreen>
        <EmptyState
          icon={WifiOff}
          title="Can't reach Hearth"
          description={errorMessage(error)}
          action={
            <Button variant="primary" loading={isRefetching} onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </FullScreen>
    );
  }
  if (!user) {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return children ?? <Outlet />;
}

/** Page-level guard for routes that need a permission; shows an explanation instead of the page. */
export function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useAuth();
  if (!can(permission)) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="You don't have access to this page"
        description="Ask an administrator if you need this permission."
      />
    );
  }
  return children;
}

function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-canvas">{children}</div>;
}
